"use client";

import Link from "@/infrastructure/router/Link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	Avatar,
	EmptyState,
	GlassButton,
	GlassCard,
	GlassIconButton,
	GlassInputBar,
	NavigationBar,
	Skeleton,
	Toast,
} from "@/components/design-system/ArtistDesignSystem";
import {
	deleteLocalNote,
	loadLocalNote,
	setLocalNoteLiked,
	type LocalNoteAsset,
} from "@/domain/social/LocalNoteStore";
import {
	type Comment,
	offlineSocialRepository,
	type SocialRepository,
} from "@/domain/social/SocialRepository";

interface RenderedNote {
	readonly asset: LocalNoteAsset;
	readonly mediaURLs: readonly string[];
}

export function CommunityThread({
	id,
	repository = offlineSocialRepository,
}: Readonly<{ id: string; repository?: SocialRepository }>) {
	const auth = useArtistSession();
	const [note, setNote] = useState<RenderedNote | null | undefined>(undefined);
	const [comments, setComments] = useState<readonly Comment[] | null>(null);
	const [draft, setDraft] = useState("");
	const [reply, setReply] = useState<{ readonly id: string; readonly name: string } | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	const scope = auth.scope;
	const load = useCallback(async () => {
		if (auth.phase !== "user" || scope === null) {
			setNote(null);
			setComments([]);
			return;
		}
		setNote(undefined);
		setComments(null);
		setError(null);
		try {
			const [asset, page] = await Promise.all([
				loadLocalNote(scope, id),
				repository.loadComments(scope, id, 0),
			]);
			if (asset === null) {
				setNote(null);
				setComments([]);
				return;
			}
			setNote({
				asset,
				mediaURLs: asset.media.map((media) => URL.createObjectURL(media.blob)),
			});
			setComments(page.comments);
		} catch {
			setNote(null);
			setComments([]);
			setError("笔记暂时无法载入，请重试。");
		}
	}, [auth.phase, id, repository, scope]);

	useEffect(() => {
		void load();
	}, [load]);

	useEffect(() => {
		return () => {
			note?.mediaURLs.forEach((url) => {
				URL.revokeObjectURL(url);
			});
		};
	}, [note]);

	const commentCount = useMemo(() => countComments(comments ?? []), [comments]);

	async function submitComment(): Promise<void> {
		if (scope === null || submitting || draft.trim().length === 0) return;
		setSubmitting(true);
		setError(null);
		try {
			const result = await repository.postComment(
				scope,
				id,
				draft,
				reply?.id,
			);
			if (result.kind === "failed") throw new Error("comment-save-failed");
			setComments((current) =>
				reply === null
					? [...(current ?? []), result.comment]
					: appendReply(current ?? [], reply.id, result.comment),
			);
			setDraft("");
			setReply(null);
			setNotice("评论已发布。");
		} catch {
			setError("评论未保存，请重试。");
		} finally {
			setSubmitting(false);
		}
	}

	async function toggleNoteLike(): Promise<void> {
		if (scope === null || note === null || note === undefined) return;
		const previous = note.asset;
		const liked = !previous.isLiked;
		setNote({
			...note,
			asset: {
				...previous,
				isLiked: liked,
				likeCount: Math.max(0, previous.likeCount + (liked ? 1 : -1)),
			},
		});
		try {
			const saved = await setLocalNoteLiked(scope, id, liked);
			setNote((current) => current === null || current === undefined ? current : { ...current, asset: saved });
		} catch {
			setNote((current) => current === null || current === undefined ? current : { ...current, asset: previous });
			setError("点赞未保存，请重试。");
		}
	}

	async function toggleCommentLike(comment: Comment): Promise<void> {
		if (scope === null) return;
		const liked = !comment.liked;
		const previousLikes = comment.likes;
		setComments((current) => updateComment(current ?? [], comment.id, (item) => ({
			...item,
			liked,
			likes: Math.max(0, item.likes + (liked ? 1 : -1)),
		})));
		try {
			const result = await repository.likeComment(scope, comment.id, liked);
			if (result.kind === "failed") throw new Error("comment-like-failed");
		} catch {
			setComments((current) => updateComment(current ?? [], comment.id, (item) => ({
				...item,
				liked: comment.liked,
				likes: previousLikes,
			})));
			setError("评论点赞未保存，请重试。");
		}
	}

	async function deleteNote(): Promise<void> {
		if (scope === null || note === null || note === undefined) return;
		try {
			await deleteLocalNote(scope, id);
			setNote(null);
			setComments([]);
			setNotice("笔记已删除。");
		} catch {
			setError("笔记删除失败，请重试。");
		}
	}

	if (auth.phase === "loading" || note === undefined || comments === null) {
		return <ThreadSkeleton />;
	}
	if (auth.phase !== "user" || scope === null) {
		return (
			<section className="native-page community-thread-page">
				<NavigationBar title="笔记" backHref="/community" />
				<EmptyState
					title="登录后查看笔记"
					message="评论、点赞和媒体均按账户隔离保存。"
					symbol={<ArtistIcon name="person" size={40} />}
				/>
				<Link className="primary-action" href="/auth">登录</Link>
			</section>
		);
	}
	if (note === null) {
		return (
			<section className="native-page community-thread-page">
				<NavigationBar title="笔记" backHref="/community" />
				{error === null ? null : <Toast style="error">{error}</Toast>}
				{notice === null ? null : <Toast style="success">{notice}</Toast>}
				<EmptyState
					title="找不到这条笔记"
					message="笔记可能已删除，或属于另一个账户。"
					symbol={<ArtistIcon name="message" size={40} />}
				/>
			</section>
		);
	}

	const asset = note.asset;
	return (
		<section className="native-page community-thread-page" aria-label={asset.authorName}>
			<NavigationBar
				title={asset.authorName}
				backHref="/community"
				trailing={
					<GlassIconButton label="删除笔记" onClick={() => void deleteNote()}>
						<ArtistIcon name="trash" size={20} />
					</GlassIconButton>
				}
			/>
			{error === null ? null : <Toast style="error">{error}</Toast>}
			{notice === null ? null : <Toast style="success">{notice}</Toast>}
			<div className="community-thread-body">
				<article className="community-thread-post">
					<header className="community-thread-post__author">
						<Avatar name={asset.authorName} size={40} />
						<span className="community-thread-post__author-meta">
							<strong>{asset.authorName}</strong>
							<small><time dateTime={new Date(asset.createdAt).toISOString()}>{formatTimestamp(asset.createdAt)}</time></small>
						</span>
					</header>
					<p className="community-thread-post__content">{asset.body}</p>
					{note.mediaURLs.length === 0 ? null : <ThreadMedia urls={note.mediaURLs} />}
					<footer className="community-thread-post__actions">
						<button type="button" className="note-feed-action" aria-label={asset.isLiked ? "取消点赞" : "点赞"} onClick={() => void toggleNoteLike()}>
							<ArtistIcon name="heart" size={20} strokeWidth={asset.isLiked ? 0 : 1.8} />
							<span>{asset.likeCount}</span>
						</button>
						<span
							className="note-feed-action"
							role="status"
							aria-label={`${commentCount} 条评论`}
						>
							<ArtistIcon name="message" size={20} />
							<span>{commentCount}</span>
						</span>
						<button
							type="button"
							className="note-feed-action"
							aria-label="分享"
							onClick={() => {
								if (navigator.share) void navigator.share({ title: "Artist 笔记", text: asset.body, url: window.location.href }).catch(() => undefined);
							}}
						>
							<ArtistIcon name="share" size={20} />
						</button>
					</footer>
				</article>
				<hr className="community-thread-divider" />
				<section className="community-thread-comments" aria-label="评论">
					<h2>评论 · {commentCount}</h2>
					{comments.length === 0 ? (
						<EmptyState title="暂无评论" message="发送第一条评论。" symbol={<ArtistIcon name="message" size={32} />} />
					) : (
						<ul className="community-thread-comment-list">
							{comments.map((comment) => (
								<CommentRow
									key={comment.id}
									comment={comment}
									depth={0}
									onReply={(target) => setReply({ id: target.id, name: target.author })}
									onLike={(target) => void toggleCommentLike(target)}
								/>
							))}
						</ul>
					)}
				</section>
			</div>
			<div className="community-thread-input-bar">
				{reply === null ? null : (
					<div className="community-thread-input-bar__reply">
						<span>回复 @{reply.name}</span>
						<button type="button" onClick={() => setReply(null)} aria-label="取消回复">×</button>
					</div>
				)}
				<GlassInputBar
					label="评论内容"
					placeholder={reply === null ? "说点什么…" : `回复 @${reply.name}…`}
					value={draft}
					onChange={setDraft}
					onSubmit={() => void submitComment()}
					trailing={
						<GlassButton
							type="submit"
							disabled={submitting || draft.trim().length === 0}
						>
							{submitting ? "发送中" : "发送"}
						</GlassButton>
					}
				/>
			</div>
		</section>
	);
}

function CommentRow({
	comment,
	depth,
	onReply,
	onLike,
}: Readonly<{
	comment: Comment;
	depth: number;
	onReply: (comment: Comment) => void;
	onLike: (comment: Comment) => void;
}>) {
	return (
		<li className="community-thread-comment" style={{ marginInlineStart: Math.min(depth, 2) * 20 }}>
			<GlassCard cornerRadius={12} style={{ padding: 12 }}>
				<header><strong>{comment.author}</strong><small>{comment.at}</small></header>
				<p>{comment.body}</p>
				<footer>
					<button type="button" onClick={() => onLike(comment)} aria-label={comment.liked ? "取消评论点赞" : "评论点赞"}>♥ {comment.likes}</button>
					<button type="button" onClick={() => onReply(comment)}>回复</button>
				</footer>
			</GlassCard>
			{comment.replies.length === 0 ? null : (
				<ul>
					{comment.replies.map((child) => (
						<CommentRow key={child.id} comment={child} depth={depth + 1} onReply={onReply} onLike={onLike} />
					))}
				</ul>
			)}
		</li>
	);
}

function ThreadMedia({ urls }: Readonly<{ urls: readonly string[] }>) {
	return (
		<div className="community-thread-media-grid">
			{urls.slice(0, 9).map((url) => (
				// biome-ignore lint/performance/noImgElement: owner-scoped IndexedDB Blob media.
				<img key={url} src={url} alt="" loading="lazy" />
			))}
		</div>
	);
}

function appendReply(comments: readonly Comment[], parentId: string, reply: Comment): Comment[] {
	return comments.map((comment) =>
		comment.id === parentId
			? { ...comment, replies: [...comment.replies, reply] }
			: { ...comment, replies: appendReply(comment.replies, parentId, reply) },
	);
}

function updateComment(
	comments: readonly Comment[],
	id: string,
	update: (comment: Comment) => Comment,
): Comment[] {
	return comments.map((comment) =>
		comment.id === id
			? update(comment)
			: { ...comment, replies: updateComment(comment.replies, id, update) },
	);
}

function countComments(comments: readonly Comment[]): number {
	return comments.reduce((total, comment) => total + 1 + countComments(comment.replies), 0);
}

function formatTimestamp(epochMilliseconds: number): string {
	return new Intl.DateTimeFormat(undefined, {
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(new Date(epochMilliseconds));
}

function ThreadSkeleton() {
	return (
		<section className="native-page community-thread-page" aria-busy="true" aria-label="正在载入帖子">
			<NavigationBar title="笔记" backHref="/community" />
			<div className="community-thread-body">
				<GlassCard style={{ display: "grid", gap: 12 }}>
					<div style={{ display: "flex", gap: 12, alignItems: "center" }}>
						<Skeleton shape="circle" width={40} height={40} />
						<Skeleton shape="rectangle" width={140} height={14} />
					</div>
					<Skeleton shape="rectangle" width="100%" height={100} />
				</GlassCard>
			</div>
		</section>
	);
}
